import {
  ConciliationChange,
  ConciliationChangeValue,
} from "@/@types/conciliation-history";
import { ConciliationChangeFieldEnum } from "@/constants/conciliation-history";

import { ChangeValueFormatter, JustificationNames } from "../types";
import { formatAmount } from "./amount-display";
import {
  CHANGE_BOOLEAN_LABELS,
  CHANGE_EMPTY_VALUE_LABELS,
  CHANGE_FIELD_LABELS,
  ITEM_ID_PREFIX,
  NOT_AVAILABLE,
  TEXT_SEPARATORS,
} from "./messages";
import { statusLabelOf } from "./status-options";

export const itemIdLabelOf = (id: number | string): string =>
  `${ITEM_ID_PREFIX}${id}`;

const booleanFormatter: ChangeValueFormatter = (value: ConciliationChangeValue): string =>
  CHANGE_BOOLEAN_LABELS[String(value)] ?? String(value);

const amountFormatter: ChangeValueFormatter = (value: ConciliationChangeValue): string => {
  if (typeof value === "boolean") return NOT_AVAILABLE;

  return formatAmount(value);
};

const itemIdFormatter: ChangeValueFormatter = (value: ConciliationChangeValue): string =>
  itemIdLabelOf(String(value));

const emptyValueOf = (field: ConciliationChangeFieldEnum): string =>
  CHANGE_EMPTY_VALUE_LABELS[field] ?? NOT_AVAILABLE;

const NO_JUSTIFICATION_ID: string = "0";

const FIELD_FORMATTERS: Readonly<
  Partial<Record<ConciliationChangeFieldEnum, ChangeValueFormatter>>
> = {
  [ConciliationChangeFieldEnum.JUSTIFICATION_ID]: (
    value: ConciliationChangeValue,
    names: JustificationNames
  ): string =>
    String(value) === NO_JUSTIFICATION_ID
      ? emptyValueOf(ConciliationChangeFieldEnum.JUSTIFICATION_ID)
      : names[String(value)] ?? itemIdFormatter(value, names),
  [ConciliationChangeFieldEnum.IS_LOSS]: booleanFormatter,
  [ConciliationChangeFieldEnum.IS_PAYROLL_DISCOUNT]: booleanFormatter,
  [ConciliationChangeFieldEnum.AMOUNT]: amountFormatter,
  [ConciliationChangeFieldEnum.ADJUSTMENT_AMOUNT]: amountFormatter,
  [ConciliationChangeFieldEnum.STATUS]: (value: ConciliationChangeValue): string =>
    statusLabelOf(String(value)),
  [ConciliationChangeFieldEnum.CONCILIATED_DIVERGENCE_ID]: itemIdFormatter,
};

const defaultFormatter: ChangeValueFormatter = (value: ConciliationChangeValue): string =>
  String(value);

export const changeValueOf = (
  field: ConciliationChangeFieldEnum,
  value: ConciliationChangeValue,
  names: JustificationNames
): string => {
  if (value === null || value === undefined) return emptyValueOf(field);

  return (FIELD_FORMATTERS[field] ?? defaultFormatter)(value, names);
};

export const changeTransitionOf = (
  change: ConciliationChange,
  names: JustificationNames
): string =>
  [
    changeValueOf(change.field, change.before, names),
    changeValueOf(change.field, change.after, names),
  ].join(TEXT_SEPARATORS.TRANSITION);

export const changeLabelOf = (
  change: ConciliationChange,
  names: JustificationNames
): string =>
  [
    CHANGE_FIELD_LABELS[change.field] ?? change.field,
    changeTransitionOf(change, names),
  ].join(TEXT_SEPARATORS.LABEL);
